// credit to https://github.com/mattrothenberg/use-gauge

import { useCallback, useEffect, useMemo, useState } from "react";

import { degreesToRadians, makeTickMarks, polarToCartesian } from "./utils";
import { useScreenSize } from "../../../utils/screen";

export interface IDelimiter {
  color: string;
  startAngle: number;
  endAngle: number;
}

interface IUseGaugeProps {
  delimiters: IDelimiter[];
  diameter: number;
  startAngle: number;
  endAngle: number;
  domain: [number, number];
}

interface IGetNeedleParams {
  value: number;
  baseRadius: number;
  tipRadius: number;
}

interface IGetTickPropsParams {
  length: number;
  angle: number;
}

interface IGetLabelPropsParams {
  angle: number;
  offset: number;
}

interface IGetArcPropsParams {
  offset?: number;
  startAngle: number;
  endAngle: number;
}

export function useGauge(props: IUseGaugeProps) {
  const { startAngle, endAngle, delimiters, diameter: initialDiameter, domain } = props;

  const { isLaptopScreen, isSmallScreen, isLargeScreen, isExtraLargeScreen } = useScreenSize();
  const [diameter, setDiameter] = useState(initialDiameter);

  const radius = diameter / 2;
  const [minValue, maxValue] = domain;

  const ticks = useMemo(() => {
    return makeTickMarks(startAngle, endAngle, delimiters).reverse();
  }, [startAngle, endAngle]);

  const getLabelProps = useCallback(
    (params: IGetLabelPropsParams) => {
      const { angle, offset } = params;
      const p1 = polarToCartesian(0, 0, radius - offset, angle + 90);

      return {
        x: p1.x,
        y: p1.y,
        dominantBaseline: "middle",
        textAnchor: "middle",
      };
    },
    [diameter, radius],
  );

  const getTickProps = useCallback(
    (params: IGetTickPropsParams) => {
      const { length, angle } = params;
      const p1 = polarToCartesian(0, 0, radius, angle + 90);
      const p2 = polarToCartesian(0, 0, radius + length, angle + 90);

      return {
        key: `tick-${angle}`,
        x1: p1.x,
        x2: p2.x,
        y1: p1.y,
        y2: p2.y,
      };
    },
    [ticks, diameter, radius],
  );

  const angleToValue = (angle: number) => {
    const angleRange = endAngle - startAngle;
    const valueRange = maxValue - minValue;
    const value = minValue + ((angle - startAngle) / angleRange) * valueRange;
    return Math.round(value);
  };

  const valueToAngle = (value: number) => {
    const angleRange = endAngle - startAngle;
    const valueRange = maxValue - minValue;
    const angle = startAngle + ((value - minValue) / valueRange) * angleRange;
    return Math.round(angle);
  };

  const getArcProps = useCallback(
    (params: IGetArcPropsParams) => {
      const { offset = 0, startAngle, endAngle, ...rest } = params;

      const start = polarToCartesian(0, 0, radius + offset, startAngle + 90);
      const end = polarToCartesian(0, 0, radius + offset, endAngle + 90);

      const largeArcFlag = endAngle - startAngle < 180 ? "0" : "1";

      const d = [
        "M",
        start.x - 0.001,
        start.y,
        "A",
        radius + offset,
        radius + offset,
        0,
        largeArcFlag,
        1,
        end.x,
        end.y,
      ].join(" ");

      return {
        d,
        ...rest,
      };
    },
    [diameter, radius],
  );

  const getNeedleProps = useCallback(
    (params: IGetNeedleParams) => {
      const { value, baseRadius, tipRadius } = params;
      const angle = valueToAngle(value);

      const baseCircleCenter = {
        x: 0,
        y: 0,
      };

      const tipCircleCenter = polarToCartesian(0, 0, radius, angle + 90);

      return {
        base: {
          r: baseRadius,
          cx: baseCircleCenter.x,
          cy: baseCircleCenter.y,
        },
        tip: {
          r: tipRadius,
          cx: tipCircleCenter.x,
          cy: tipCircleCenter.y,
        },
        points: [
          [
            baseCircleCenter.x + baseRadius * Math.cos(degreesToRadians(angle)),
            baseCircleCenter.y + baseRadius * Math.sin(degreesToRadians(angle)),
          ],
          [
            tipCircleCenter.x + tipRadius * Math.cos(degreesToRadians(angle)),
            tipCircleCenter.y + tipRadius * Math.sin(degreesToRadians(angle)),
          ],
          [
            tipCircleCenter.x + tipRadius * Math.cos(degreesToRadians(angle - 180)),
            tipCircleCenter.y + tipRadius * Math.sin(degreesToRadians(angle - 180)),
          ],
          [
            baseCircleCenter.x + baseRadius * Math.cos(degreesToRadians(angle - 180)),
            baseCircleCenter.y + baseRadius * Math.sin(degreesToRadians(angle - 180)),
          ],
        ]
          .map(([x, y]) => `${x},${y}`)
          .join(" "),
      };
    },
    [valueToAngle, diameter, radius],
  );

  const calculateDiameterForDirection = useCallback(
    (startAngle: number, deg: number) => {
      const angle = startAngle - deg;
      return (Math.cos(degreesToRadians(angle)) * diameter) / 2;
    },
    [diameter],
  );

  const getSVGProps = () => {
    const getDistanceForDirection = (deg: number) => {
      if (startAngle < deg && endAngle > deg) return diameter / 2;
      const startAngleDistance = calculateDiameterForDirection(startAngle + 90, deg + 90);
      const endAngleDistance = calculateDiameterForDirection(endAngle + 90, deg + 90);
      return Math.max(0, startAngleDistance, endAngleDistance);
    };

    const [top, right, bottom, left] = [
      getDistanceForDirection(180),
      getDistanceForDirection(270),
      getDistanceForDirection(0),
      getDistanceForDirection(90),
    ];

    const width = left + right;
    const height = top + bottom;

    const viewBox = [-left, -top, width, height].join(" ");

    return {
      width,
      height,
      viewBox,
    };
  };

  useEffect(() => {
    const newDiameter = isExtraLargeScreen
      ? initialDiameter + 220
      : isLargeScreen
        ? initialDiameter + 90
        : isLaptopScreen
          ? initialDiameter - 100
          : isSmallScreen
            ? initialDiameter - 130
            : initialDiameter - 150;
    setDiameter(newDiameter);
  }, [isLaptopScreen, isSmallScreen, isLargeScreen, isExtraLargeScreen, initialDiameter]);

  return {
    ticks,
    getTickProps,
    getLabelProps,
    valueToAngle,
    angleToValue,
    getArcProps,
    getNeedleProps,
    getSVGProps,
  };
}
